/**
 * uiLanguage.ts
 *
 * Minimal shared UI-language preference for public-facing legal surfaces (the
 * cookie banner + the login consent line). Persisted to localStorage and shared
 * across components via a window event so the banner's EN/FR toggle keeps the
 * login text in sync without a full i18n framework.
 */
import { useSyncExternalStore } from 'react';

export type UiLang = 'en' | 'fr';

const STORAGE_KEY = 'ftp.lang';
const CHANGE_EVENT = 'ftp-lang-change';

function read(): UiLang {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'fr' ? 'fr' : 'en';
  } catch {
    return 'en';
  }
}

function subscribe(cb: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, cb);
  window.addEventListener('storage', cb); // sync across tabs
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb);
    window.removeEventListener('storage', cb);
  };
}

/** Set the UI language and notify all subscribers in this tab. */
export function setUiLang(lang: UiLang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // localStorage unavailable — fall through; the event still updates this tab.
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/** Reactive current UI language ('en' | 'fr'), defaulting to English. */
export function useUiLang(): UiLang {
  return useSyncExternalStore(subscribe, read, () => 'en');
}
